import { useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import type { ClassificationVerdict, Content, GameState, Problem } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * Étape 2 — Classer.
 *
 * Deux façons de faire, toujours : le glisser-déposer au doigt (dnd-kit), et
 * « je choisis un problème, je choisis sa catégorie » au clic ou au clavier.
 * La seconde n'est pas un pis-aller : c'est ce qui rend l'étape utilisable au
 * clavier, et ce qui sauve la séance quand un écran tactile répond mal.
 */

function Etiquette({
  probleme,
  selectionne,
  verdict,
  onSelectionner,
}: {
  probleme: Problem;
  selectionne: boolean;
  verdict: ClassificationVerdict | undefined;
  onSelectionner: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: probleme.id });

  const couleur = verdict
    ? verdict.correcte
      ? 'border-emerald-400 bg-emerald-50'
      : 'border-rose-400 bg-rose-50'
    : selectionne
      ? 'border-sky-600 bg-sky-50'
      : 'border-slate-300 bg-white';

  return (
    <button
      // Les attributs de dnd-kit viennent d'abord : « aria-pressed » décrit ici
      // la sélection au clavier, pas l'état de glissement, et doit l'emporter.
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      type="button"
      aria-pressed={selectionne}
      onClick={onSelectionner}
      className={`min-h-touch w-full cursor-grab rounded-xl border-2 px-3 py-2 text-left font-medium text-slate-800 ${couleur} ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {probleme.titre}
      {verdict && (
        <span className="mt-1 block text-sm font-normal">
          <span aria-hidden="true">{verdict.correcte ? '✓ ' : '✕ '}</span>
          {verdict.correcte ? T.classer.juste : T.classer.faux}
        </span>
      )}
    </button>
  );
}

export function EtapeClasser({
  content,
  partie,
  verdicts,
  onClasser,
  onValider,
}: {
  content: Content;
  partie: GameState;
  verdicts: readonly ClassificationVerdict[];
  onClasser: (problemId: string, categoryId: string) => void;
  onValider: () => void;
}) {
  const [selection, setSelection] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const parId = new Map(content.problemes.map((p) => [p.id, p]));
  const decouverts = partie.discoveredProblems
    .map((id) => parId.get(id))
    .filter((p): p is Problem => p !== undefined);

  const verdictsParId = new Map(verdicts.map((v) => [v.problemId, v]));
  const aRanger = decouverts.filter((p) => partie.classification[p.id] === undefined);
  const toutRange = aRanger.length === 0;

  const deposer = (problemId: string, categoryId: string): void => {
    onClasser(problemId, categoryId);
    setSelection(null);
  };

  const surFinDeGlisser = (evenement: DragEndEvent): void => {
    const categoryId = evenement.over?.id;
    if (typeof categoryId === 'string' && typeof evenement.active.id === 'string') {
      deposer(evenement.active.id, categoryId);
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={surFinDeGlisser}>
      <div className="space-y-4">
        <p className="text-slate-700">{T.classer.consigneSelection}</p>

        <section className="rounded-2xl border-2 border-slate-200 bg-white p-3">
          <h3 className="font-bold text-slate-900">
            {T.classer.aRanger} ({aRanger.length})
          </h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {aRanger.map((probleme) => (
              <li key={probleme.id}>
                <Etiquette
                  probleme={probleme}
                  selectionne={selection === probleme.id}
                  verdict={undefined}
                  onSelectionner={() =>
                    setSelection((precedent) => (precedent === probleme.id ? null : probleme.id))
                  }
                />
              </li>
            ))}
          </ul>
        </section>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {content.categories_problemes.map((categorie) => {
            const dedans = decouverts.filter((p) => partie.classification[p.id] === categorie.id);

            return (
              <CategorieAvecProblemes
                key={categorie.id}
                categorie={categorie}
                problemes={dedans}
                verdictsParId={verdictsParId}
                selection={selection}
                onDeposer={() => selection && deposer(selection, categorie.id)}
                onSelectionner={(problemId) =>
                  setSelection((precedent) => (precedent === problemId ? null : problemId))
                }
              />
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {verdicts.length > 0 && (
            <p aria-live="polite" className="font-semibold text-slate-800">
              {T.classer.resultat} : {verdicts.filter((v) => v.correcte).length} / {verdicts.length}
            </p>
          )}

          <button
            type="button"
            disabled={!toutRange}
            onClick={onValider}
            className="min-h-touch rounded-xl bg-sky-600 px-6 font-semibold text-white disabled:bg-slate-300 disabled:text-slate-500"
          >
            {T.classer.valider}
          </button>
        </div>

        {verdicts.length > 0 && (
          <ul className="space-y-2">
            {verdicts
              .filter((verdict) => !verdict.correcte)
              .map((verdict) => {
                const probleme = parId.get(verdict.problemId);
                const attendue = content.categories_problemes.find(
                  (c) => c.id === verdict.attendue,
                );

                return (
                  <li
                    key={verdict.problemId}
                    className="rounded-xl border border-rose-200 bg-rose-50 p-3"
                  >
                    <p className="font-semibold text-slate-900">{probleme?.titre}</p>
                    <p className="text-sm text-slate-700">
                      {verdict.peutReessayer
                        ? T.classer.secondeChance
                        : `${T.classer.bonneReponse} : ${attendue?.nom ?? verdict.attendue}`}
                    </p>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </DndContext>
  );
}

/** Une catégorie et les problèmes qu'elle contient. */
function CategorieAvecProblemes({
  categorie,
  problemes,
  verdictsParId,
  selection,
  onDeposer,
  onSelectionner,
}: {
  categorie: { id: string; nom: string; couleur: string };
  problemes: readonly Problem[];
  verdictsParId: ReadonlyMap<string, ClassificationVerdict>;
  selection: string | null;
  onDeposer: () => void;
  onSelectionner: (problemId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: categorie.id });

  return (
    <section
      ref={setNodeRef}
      aria-label={categorie.nom}
      className={`rounded-2xl border-2 p-3 transition ${
        isOver ? 'border-sky-600 bg-sky-50' : 'border-slate-200 bg-white'
      }`}
    >
      <h3 className="flex items-center gap-2 font-bold text-slate-900">
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 rounded-full"
          style={{ backgroundColor: categorie.couleur }}
        />
        {categorie.nom}
      </h3>

      <button
        type="button"
        disabled={selection === null}
        onClick={onDeposer}
        aria-label={`${T.classer.placerDans} ${categorie.nom}`}
        className="mt-2 min-h-touch w-full rounded-xl border-2 border-dashed border-slate-300 px-3 text-sm font-semibold text-slate-600 disabled:opacity-40"
      >
        {T.classer.placerDans}
      </button>

      <ul className="mt-2 space-y-2">
        {problemes.map((probleme) => (
          <li key={probleme.id}>
            <Etiquette
              probleme={probleme}
              selectionne={selection === probleme.id}
              verdict={verdictsParId.get(probleme.id)}
              onSelectionner={() => onSelectionner(probleme.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
